import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

/**
 * The unsaved-changes guard — FR-13, and W-7 for what it does not cover.
 *
 * A screen registers what it would lose; anything that navigates asks first.
 *
 * **Why not `useBlocker`.** React Router 7 ships one, and it is the right tool:
 * it catches browser back too. It also calls `useDataRouterContext`, so it
 * throws outside a data router, and this app mounts `<BrowserRouter>`. Adopting
 * it means migrating every route in `App.tsx` to `createBrowserRouter` — which
 * this feature's spec lists as a non-goal, and which is not a change to make
 * underneath someone who is mid-way through authoring a library.
 *
 * So the guard covers the navigations Studio itself owns: nav items, breadcrumb
 * ancestors, CANCEL. **Browser back is not covered**, and `beforeunload` stays
 * for tab close. That gap is real and is recorded rather than papered over.
 */

type Registration = {
  dirty: boolean;
  /** Persist and continue. Label differs by screen: SAVE DRAFT vs SAVE CHANGES. */
  save: () => Promise<void> | void;
  saveLabel: string;
  /** Throw the edits away and continue. */
  discard: () => void;
};

type GuardState = {
  /** Held navigation, waiting on the author's answer. */
  pending: boolean;
  registration: Registration | null;
  /** Navigate, asking first if anything would be lost. */
  request: (run: () => void) => void;
  keepEditing: () => void;
  saveAndContinue: () => Promise<void>;
  discardAndContinue: () => void;
  register: (r: Registration | null) => void;
};

const Context = createContext<GuardState | null>(null);

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [pendingRun, setPendingRun] = useState<{ run: () => void } | null>(null);

  // Read inside `request`, which is called from an event handler that may hold
  // a stale closure over the state value.
  const latest = useRef<Registration | null>(null);
  latest.current = registration;

  const request = useCallback((run: () => void) => {
    if (!latest.current?.dirty) {
      run();
      return;
    }
    // Wrapped in an object: `setState` with a bare function argument would call
    // it as an updater instead of storing it.
    setPendingRun({ run });
  }, []);

  const keepEditing = useCallback(() => setPendingRun(null), []);

  const saveAndContinue = useCallback(async () => {
    const run = pendingRun?.run;
    await latest.current?.save();
    setPendingRun(null);
    run?.();
  }, [pendingRun]);

  const discardAndContinue = useCallback(() => {
    const run = pendingRun?.run;
    latest.current?.discard();
    setPendingRun(null);
    run?.();
  }, [pendingRun]);

  const value = useMemo<GuardState>(
    () => ({
      pending: pendingRun !== null,
      registration,
      request,
      keepEditing,
      saveAndContinue,
      discardAndContinue,
      register: setRegistration,
    }),
    [pendingRun, registration, request, keepEditing, saveAndContinue, discardAndContinue]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useNavigationGuard(): GuardState {
  const value = useContext(Context);
  if (!value) {
    throw new Error("useNavigationGuard must be used inside <NavigationGuardProvider>");
  }
  return value;
}

/**
 * Navigate through the guard.
 *
 * Every in-app link in Studio goes through this. A `<Link>` that does not is a
 * hole - which is why the nav and the breadcrumb both use it rather than
 * trusting each caller to remember.
 */
export function useGuardedNavigate(): (to: string) => void {
  const navigate = useNavigate();
  const { request } = useNavigationGuard();
  return useCallback((to: string) => request(() => navigate(to)), [navigate, request]);
}
